
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

export default function Signup() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    communityName: '',
    email: '',
    memberCount: '',
    plan: 'pro'
  });

  const plans = {
    basic: { name: 'Basic', price: 29, members: '50' },
    pro: { name: 'Pro', price: 79, members: '200' },
    enterprise: { name: 'Enterprise', price: 199, members: 'Unlimited' }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Sign in with Google first
      const { error: authError } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/payment-processing`,
          queryParams: {
            communityName: formData.communityName,
            email: formData.email,
            memberCount: formData.memberCount,
            plan: formData.plan
          }
        }
      });

      if (authError) throw authError;
    } catch (error) {
      console.error('Error:', error);
      alert('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50 pt-24 pb-16 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Join NeighborHub
          </h1>
          <p className="text-xl text-gray-600">
            Start managing your community in minutes
          </p>
        </div>

        {/* Selected Plan Display */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8 border-2 border-teal-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 mb-1">Selected Plan</p>
              <h3 className="text-2xl font-bold text-gray-900">
                {plans[formData.plan as keyof typeof plans].name}
              </h3>
              <p className="text-gray-600 mt-1">
                Up to {plans[formData.plan as keyof typeof plans].members} members
              </p>
            </div>
            <div className="text-right">
              <p className="text-4xl font-bold text-teal-600">
                ${plans[formData.plan as keyof typeof plans].price}
              </p>
              <p className="text-gray-500">per month</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="mt-4 text-teal-600 hover:text-teal-700 text-sm font-medium whitespace-nowrap"
          >
            ← Change Plan
          </button>
        </div>

        {/* Signup Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Community Name */}
            <div>
              <label htmlFor="communityName" className="block text-sm font-semibold text-gray-700 mb-2">
                Community Name *
              </label>
              <input
                type="text"
                id="communityName"
                required
                value={formData.communityName}
                onChange={(e) => setFormData({ ...formData, communityName: e.target.value })}
                placeholder="e.g., Sunset Hills HOA"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base"
              />
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                Community Email *
              </label>
              <input
                type="email"
                id="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="admin@community.com"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base"
              />
            </div>

            {/* Member Count */}
            <div>
              <label htmlFor="memberCount" className="block text-sm font-semibold text-gray-700 mb-2">
                Number of Members *
              </label>
              <input
                type="number"
                id="memberCount"
                required
                min="1"
                value={formData.memberCount}
                onChange={(e) => setFormData({ ...formData, memberCount: e.target.value })}
                placeholder="e.g., 75"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base"
              />
            </div>

            {/* Plan Selection */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Select Your Plan *
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {Object.entries(plans).map(([key, plan]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setFormData({ ...formData, plan: key })}
                    className={`p-4 rounded-lg border-2 transition-all whitespace-nowrap ${
                      formData.plan === key
                        ? 'border-teal-500 bg-teal-50'
                        : 'border-gray-200 hover:border-teal-300'
                    }`}
                  >
                    <p className="font-bold text-gray-900">{plan.name}</p>
                    <p className="text-2xl font-bold text-teal-600 mt-2">${plan.price}</p>
                    <p className="text-sm text-gray-500 mt-1">per month</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-teal-600 hover:bg-teal-700 text-white font-semibold py-4 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-lg whitespace-nowrap"
            >
              {loading ? 'Processing...' : 'Continue with Google Sign In'}
            </button>

            {/* Trust Signals */}
            <div className="pt-6 border-t border-gray-200">
              <div className="flex items-center justify-center gap-8 text-sm text-gray-500">
                <div className="flex items-center gap-2">
                  <i className="ri-shield-check-line text-teal-600 text-lg"></i>
                  <span>Secure Payment</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-lock-line text-teal-600 text-lg"></i>
                  <span>SSL Encrypted</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-customer-service-line text-teal-600 text-lg"></i>
                  <span>24/7 Support</span>
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Additional Info */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>
            By signing up, you agree to our{' '}
            <a href="#" className="text-teal-600 hover:text-teal-700">Terms of Service</a>
            {' '}and{' '}
            <a href="#" className="text-teal-600 hover:text-teal-700">Privacy Policy</a>
          </p>
        </div>
      </div>
    </div>
  );
}
